import { memo, useMemo } from 'react';

import { MobileAssetModalNavigation } from '~/_components/MobileAssetModalNavigation';
import { PublicMobileHeader } from '~/components/AssetModal/components/PublicMobileHeader';
import { MobileCommentsPanelWrapper } from '~/components/AssetModal/MobileTabNavigation/components/MobileCommentsPanelWrapper';
import { MobileCommentsTab } from '~/components/AssetModal/MobileTabNavigation/components/MobileCommentsTab';
import { PublicMobileVersionsPanelWrapper } from '~/components/AssetModal/MobileTabNavigation/MobileVersionsPanelWrapper/PublicMobileVersionsPanelWrapper';
import { MobilePDFPageControls } from '~/components/AssetModalV2/Visualizer/PDFVisualizer/PDFControls/components/MobilePDFPageControls';
import { PublicPDFVisualizer } from '~/components/AssetModalV2/Visualizer/PDFVisualizer/PublicPDFVisualizer';
import { MobileVersionsTab } from '~/components/PrivateAssetModal/components/MobileVersionsTab';
import { PublicAssetMobileDownloadButton } from '~/components/PublicAssetModal/components/PublicAssetMobileDownloadButton';
import { PublicAssetModalInfoPanel } from '~/components/PublicAssetModal/components/PublicAssetModalInfoPanel';
import { PublicComments } from '~/components/PublicAssetModal/components/PublicCommentsPanel';
import { PublicMobileEllipsisMenu } from '~/components/PublicAssetModal/components/PublicMobileEllipsisMenu';
import { PublicPreviousAndNextArrows } from '~/components/PublicAssetModal/components/PublicPreviousAndNextArrows';
import { PublicVideoVisualizer } from '~/components/PublicAssetModal/components/PublicVideoVisualizer';
import { usePublicPermissions } from '~/components/PublicPermissionsProvider';
import { AssetModalMobileLayout } from '~/components/v2/components/asset-modal/mobile/AssetModalMobileLayout';
import { AssetModalPanel, useAssetModalPanelContext } from '~/providers/AssetModalPanelContextProvider';
import { canDownloadVersion, canSeeAssetVersions, canViewAssetDiscussions } from '~/utils/permissions/assetPermissions';

export type PublicAssetModalMobileLayoutProps = {
  /**
   * Close the open public asset modal. Supplied from the asset-modal navigation contract so the
   * close matches the surface: a route change on real public pages, editor state in the collection
   * link editor's Edit tab (a hardcoded route push there would route the author away).
   */
  onCloseAssetModal: () => void;
};

export const PublicAssetModalMobileLayout = memo(({ onCloseAssetModal }: PublicAssetModalMobileLayoutProps) => {
  const { panel, hidePanel } = useAssetModalPanelContext();

  const { permissions } = usePublicPermissions();
  const canViewComments = canViewAssetDiscussions(permissions);
  const canViewAssetVersions = canSeeAssetVersions(permissions);
  const canDownload = canDownloadVersion(permissions);

  const panelContent = useMemo(() => {
    if (panel === AssetModalPanel.COMMENTS && canViewComments) {
      return <MobileCommentsPanelWrapper onClose={hidePanel} CommentsPanel={<PublicComments />} />;
    }
    if (panel === AssetModalPanel.VERSIONS && canViewAssetVersions) {
      return <PublicMobileVersionsPanelWrapper onClose={hidePanel} />;
    }

    return null;
  }, [canViewAssetVersions, canViewComments, hidePanel, panel]);

  const content = useMemo(
    () => (
      <>
        {panelContent}
        <PublicAssetModalInfoPanel />
      </>
    ),
    [panelContent],
  );

  const navigation = useMemo(
    () => (
      <MobileAssetModalNavigation suffix={canDownload ? <PublicAssetMobileDownloadButton /> : undefined}>
        {canViewComments && <MobileCommentsTab />}
        {canViewAssetVersions && <MobileVersionsTab />}
        <PublicMobileEllipsisMenu />
      </MobileAssetModalNavigation>
    ),
    [canViewComments, canViewAssetVersions, canDownload],
  );

  const header = useMemo(() => <PublicMobileHeader onClose={onCloseAssetModal} />, [onCloseAssetModal]);

  return (
    <AssetModalMobileLayout
      PreviousAndNextArrows={PublicPreviousAndNextArrows}
      PDFVisualizer={PublicPDFVisualizer}
      VideoVisualizer={PublicVideoVisualizer}
      navigation={navigation}
      panel={content}
      header={header}
      visualizerOverlay={<MobilePDFPageControls />}
    />
  );
});

PublicAssetModalMobileLayout.displayName = 'PublicAssetModalMobileLayout';
